if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      getSelectedMainVariant() {
        const selectedVariantJson = this.mainProductInfo?.querySelector('variant-selects [data-selected-variant]')
          ?.textContent;

        if (!selectedVariantJson) return null;

        try {
          return JSON.parse(selectedVariantJson);
        } catch (error) {
          return null;
        }
      }

      getOptionNames() {
        if (this._optionNames) return this._optionNames;

        try {
          this._optionNames = this.dataset.optionNames ? JSON.parse(this.dataset.optionNames) : [];
        } catch (error) {
          this._optionNames = [];
        }

        return this._optionNames;
      }

      updateVariantSizeDisplay(variant) {
        const variantSizeDisplay = this.querySelector('[data-variant-size]');
        if (!variantSizeDisplay) return;

        if (!variant || !Array.isArray(variant.options)) {
          variantSizeDisplay.textContent = '';
          return;
        }

        const sizeOptionNames = ['breite', 'ancho', 'width', 'size'];
        const optionNames = this.getOptionNames();
        const sizeOptionIndex = optionNames.findIndex((optionName) =>
          sizeOptionNames.includes((optionName || '').toLowerCase())
        );

        let sizeOptionValue = sizeOptionIndex >= 0 ? variant.options[sizeOptionIndex] : '';

        if (!sizeOptionValue) {
          sizeOptionValue = variant.options.find((opt) => /^\d+([.,]\d+)?/.test((opt || '').trim())) || '';
        }

        variantSizeDisplay.textContent = sizeOptionValue ? ` ${sizeOptionValue}cm` : '';
      }

      setAddonImageState(wrap, image, imageUrl, altText) {
        if (!wrap || !image) return;

        const normalizedUrl = (imageUrl || '').trim();
        const hasImage = Boolean(normalizedUrl);

        if (!hasImage) {
          wrap.classList.add('is-hidden');
          wrap.hidden = true;
          wrap.style.display = 'none';
          image.removeAttribute('src');
          image.alt = '';
          return;
        }

        image.src = normalizedUrl;
        image.alt = altText || '';
        wrap.classList.remove('is-hidden');
        wrap.hidden = false;
        wrap.style.removeProperty('display');
      }

      updateBasinDisplay(detail = {}) {
        const basinImageWrap = this.querySelector('[data-sticky-basin-image-wrap]');
        const basinImage = this.querySelector('[data-sticky-basin-image]');
        const addonImageWrap = this.querySelector('[data-sticky-addon-image-wrap]');
        const addonImage = this.querySelector('[data-sticky-addon-image]');
        const basinSummary = this.querySelector('[data-sticky-bundle-summary], [data-sticky-basin-summary]');
        if (!basinImageWrap || !basinImage || !basinSummary) return;

        const basinName = (detail.basinName || '').trim();
        const addonNames = Array.isArray(detail.addonNames)
          ? detail.addonNames.filter(Boolean)
          : [];
        const addonName = (detail.addonName || addonNames.join(' + ')).trim();
        const basinImageUrl = (detail.basinImage || '').trim();
        const addonImageUrl = (detail.addonImage || '').trim();
        const hasBasin = Boolean(detail.hasBasin && basinName);
        const hasAddon = Boolean(detail.hasAddons && addonName);

        if (!hasBasin && !hasAddon) {
          this.setAddonImageState(basinImageWrap, basinImage, '', '');
          this.setAddonImageState(addonImageWrap, addonImage, '', '');
          basinSummary.hidden = true;
          basinSummary.textContent = '';
          return;
        }

        this.setAddonImageState(
          basinImageWrap,
          basinImage,
          hasBasin ? basinImageUrl : '',
          hasBasin ? basinName : ''
        );
        this.setAddonImageState(
          addonImageWrap,
          addonImage,
          hasAddon ? addonImageUrl : '',
          hasAddon ? addonName : ''
        );

        basinSummary.textContent = [basinName, addonName].filter(Boolean).join(' + ');
        basinSummary.hidden = !basinSummary.textContent;
      }

      getCachedStickyBasinState() {
        const keys = [String(this.dataset.section || ''), String(this.dataset.productId || '')].filter(Boolean);
        const caches = [
          window.__blWashbasinV2StickyState,
          window.__blAddonConfiguratorStickyState,
          window.__blProductAddonsStickyState,
        ].filter(Boolean);

        for (const cache of caches) {
          for (const key of keys) {
            if (cache[key]) return cache[key];
          }
        }

        return null;
      }

      connectedCallback() {
        this.refreshTargets();

        this.init();
        this.updateVariantSizeDisplay(this.getSelectedMainVariant());
        this.updateBasinDisplay(this.getCachedStickyBasinState() || { hasBasin: false });

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this._boundToggleStickyBarOnScroll) {
          window.removeEventListener('scroll', this._boundToggleStickyBarOnScroll);
          window.removeEventListener('resize', this._boundToggleStickyBarOnScroll);
        }

        if (this._boundToggleStickyBarAfterLoad) {
          window.removeEventListener('load', this._boundToggleStickyBarAfterLoad);
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this._boundWashbasinStickyUpdate) {
          document.removeEventListener('bl:washbasin-v2:sticky-update', this._boundWashbasinStickyUpdate);
          document.removeEventListener('bl:addon-configurator:sticky-update', this._boundWashbasinStickyUpdate);
          document.removeEventListener('bl:product-addons:sticky-update', this._boundWashbasinStickyUpdate);
        }
      }

      init() {
        this.refreshTargets();

        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        this._boundToggleStickyBarOnScroll = this.toggleStickyBarOnScroll.bind(this);
        this._boundToggleStickyBarAfterLoad = this.toggleStickyBarOnScroll.bind(this);
        window.addEventListener('scroll', this._boundToggleStickyBarOnScroll, { passive: true });
        window.addEventListener('resize', this._boundToggleStickyBarOnScroll);
        window.addEventListener('load', this._boundToggleStickyBarAfterLoad, { once: true });
        this.toggleStickyBarOnScroll();
        requestAnimationFrame(() => {
          this.toggleStickyBarOnScroll();
          requestAnimationFrame(() => this.toggleStickyBarOnScroll());
        });
        this.syncWithMainProductForm();
        this.bindWashbasinStickyUpdates();
      }

      refreshTargets() {
        this.productForm = document.querySelector('.main-product-form');
        this.productFormActions =
          this.productForm?.querySelector('button.product-form__submit[type="submit"]') ||
          this.productForm?.querySelector('button.product-form__submit[type="button"]') ||
          this.productForm?.querySelector('[name="add"]') ||
          this.productForm;
        this.mainProductInfo = document.querySelector('product-info');
        this.galleryBlock =
          this.mainProductInfo?.querySelector('media-gallery, product-media-gallery, .media-gallery') ||
          document.querySelector('media-gallery, product-media-gallery, .media-gallery');
        this.priceBlock = this.getPriceBlock();
      }

      getPriceBlock() {
        return (
          this.mainProductInfo?.querySelector('.product__block--price') ||
          this.mainProductInfo?.querySelector(`#price-${this.dataset.section}`) ||
          document.querySelector('product-info .product__block--price') ||
          document.getElementById(`price-${this.dataset.section}`) ||
          this.mainProductInfo?.querySelector('[id^="price-"]') ||
          document.querySelector('product-info [id^="price-"]')
        );
      }

      bindWashbasinStickyUpdates() {
        this._boundWashbasinStickyUpdate = (event) => {
          const detail = event.detail || {};
          const isMatchingSection = !detail.sectionId || String(detail.sectionId) === String(this.dataset.section);
          const isMatchingProduct = !detail.productId || String(detail.productId) === String(this.dataset.productId);
          if (!isMatchingSection && !isMatchingProduct) return;

          this.updateBasinDisplay(detail);
        };

        document.addEventListener('bl:washbasin-v2:sticky-update', this._boundWashbasinStickyUpdate);
        document.addEventListener('bl:addon-configurator:sticky-update', this._boundWashbasinStickyUpdate);
        document.addEventListener('bl:product-addons:sticky-update', this._boundWashbasinStickyUpdate);
      }

      isElementFullyVisible(element, bottomOffset = 0) {
        if (!element) return false;

        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom - bottomOffset <= viewportHeight &&
          rect.right <= viewportWidth
        );
      }

      getFooterElement() {
        return (this._footerElement = this._footerElement || document.querySelector('footer.footer'));
      }

      isFooterOverlappingSticky() {
        const footer = this.getFooterElement();
        if (!footer) return false;

        const footerRect = footer.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const stickyRect = this.getBoundingClientRect();
        const stickyStyles = window.getComputedStyle(this);
        const bottomOffset = parseFloat(stickyStyles.bottom) || 0;
        const stickyTop = viewportHeight - bottomOffset - stickyRect.height;
        const stickyBottom = viewportHeight - bottomOffset;

        return footerRect.top < stickyBottom && footerRect.bottom > stickyTop;
      }

      toggleStickyBarOnScroll() {
        this.refreshTargets();

        if (!this.productFormActions) return;

        const rect = this.productFormActions.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const galleryRect = this.galleryBlock?.getBoundingClientRect();
        const priceRect = this.priceBlock?.getBoundingClientRect();
        const hasScrolledPastGallery = !galleryRect || galleryRect.bottom <= 0;
        const hasScrolledPastPrice = !priceRect || priceRect.bottom <= 0;
        const hasPriceStartedLeavingViewport = !priceRect || priceRect.top < 0;
        const isPriceFullyVisible = this.isElementFullyVisible(this.priceBlock, 0);
        const isButtonBelowViewport = rect.top >= viewportHeight;
        const isButtonAboveViewport = rect.bottom <= 0;
        const isFooterOverlappingSticky = this.isFooterOverlappingSticky();
        const isDesktop = window.innerWidth >= 768;

        // On mobile, the sticky gallery covers the top portion of the viewport.
        // The price is hidden when it scrolls behind the gallery, not when it
        // leaves the viewport. Use the exposed threshold from BL-mobile-sticky-gallery.
        const stickyGalleryBottom = window.__blStickyGalleryVisibleBottom || 0;
        const isPriceHiddenBehindGallery = !priceRect || priceRect.bottom <= stickyGalleryBottom;
        const isButtonHiddenByGallery = stickyGalleryBottom > 0 && rect.bottom <= stickyGalleryBottom;

        const shouldShow = isDesktop
          ? (
              !isFooterOverlappingSticky &&
              (
                isButtonAboveViewport ||
                (isButtonBelowViewport && hasPriceStartedLeavingViewport && !isPriceFullyVisible)
              )
            )
          : (
              (stickyGalleryBottom > 0 ? isPriceHiddenBehindGallery : hasScrolledPastPrice) &&
              (isButtonBelowViewport || isButtonAboveViewport || isButtonHiddenByGallery) &&
              !isFooterOverlappingSticky
            );

        this.classList.toggle(this.classes.isOpen, shouldShow);
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;

          this.currentVariant = variant;
          this.updateVariantSizeDisplay(variant);

          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
